import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { I18nText, Spinner } from '@welldev-group/react-comp-lib';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
} from '@welldev-group/rcl-table';
import ConfirmationModal from '../../../../../../shared/components/ConfirmationModal';
import TablePlaceholder from '../../../../../../shared/components/Placeholder/TablePlaceHolder';
import EmptyTableContent from '../../../../../../shared/components/EmptyTableContent';
import UserTableRow from './UserTableRow';
import SanitizedHTML from '../../../../../../shared/components/SanitizedHTML';

const UserTable = ({
  users,
  isLoading,
  verifyEmail,
  reverifyEmail,
  status,
  deleteUser,
  reactivateUser,
  resendInvite,
  permissionOptions,
  permissionTemplateOptions,
  fetchUsers,
  currentUserId,
  userAffiliateId,
  signedInAsSuperAdmin,
}) => {
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [verificationType, setVerificationType] = useState(null);
  const [selectedUser, setSelectedUser] = useState({});
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [isResendInviteModalOpen, setIsResendInviteModalOpen] = useState(false);
  const [
    isReactivateConfirmationModalOpen,
    setIsReactivateConfirmationModalOpen,
  ] = useState(false);
  const [
    isDeactivateConfirmationModalOpen,
    setIsDeactivateConfirmationModalOpen,
  ] = useState(false);
  const SUCCESS = 'success';
  const DANGER = 'danger';
  const ACTIVE_STATE = 'active';
  const DEACTIVATED_STATE = 'deactivated';
  const PENDING_STATE = 'pending';

  const verificationHeader = (resendInvitation = false) => {
    let message = null;
    if (!isModalLoading) {
      if (verificationType === SUCCESS) {
        message = resendInvitation ? (
          <I18nText id="affiliate_three_sixty.affiliate.users.resend_invite.success.header" />
        ) : (
          <I18nText id="affiliate_three_sixty.affiliate.users.verify.success.header" />
        );
      } else if (verificationType === DANGER) {
        message = resendInvitation ? (
          <I18nText id="affiliate_three_sixty.affiliate.users.resend_invite.fail.header" />
        ) : (
          <I18nText id="affiliate_three_sixty.affiliate.users.verify.fail.header" />
        );
      }
    }
    return message;
  };

  const verificationSubHeader = (resendInvite = false) => {
    let content;
    if (isModalLoading) {
      content = (
        <>
          <Spinner bgColor="neutral" />
          <span className="col-grid col-bleed-x">
            {resendInvite ? (
              <I18nText id="affiliate_three_sixty.affiliate.users.resend_invite.sending_message" />
            ) : (
              <I18nText id="affiliate_three_sixty.affiliate.users.verify.sending_message" />
            )}
          </span>
        </>
      );
    } else {
      switch (verificationType) {
        case SUCCESS:
          content = (
            <>
              {resendInvite ? (
                <I18nText id="affiliate_three_sixty.affiliate.users.resend_invite.success.subheader" />
              ) : (
                <I18nText id="affiliate_three_sixty.affiliate.users.verify.success.subheader" />
              )}
              <div className="affiliate-user-list__verify-box">
                {selectedUser.email}
              </div>
              {!resendInvite && (
                <I18nText id="affiliate_three_sixty.affiliate.users.verify.message" />
              )}
            </>
          );
          break;
        case DANGER:
          content = (
            <>
              {resendInvite ? (
                <I18nText id="affiliate_three_sixty.affiliate.users.resend_invite.fail.subheader" />
              ) : (
                <I18nText id="affiliate_three_sixty.affiliate.users.verify.fail.subheader" />
              )}
              <div className="affiliate-user-list__verify-box">
                {selectedUser.email}
              </div>
              {!resendInvite && (
                <I18nText id="affiliate_three_sixty.affiliate.users.verify.message" />
              )}
            </>
          );
          break;
        default:
          return null;
      }
    }
    return content;
  };

  const processVerification = (user, reverification = false) => {
    let verificationAction;
    setIsModalOpen(true);
    setIsModalLoading(true);
    setSelectedUser(user);
    if (reverification) {
      verificationAction = reverifyEmail;
    } else {
      verificationAction = verifyEmail;
    }
    verificationAction(user.id)
      .then(({ data }) => {
        if (data.error === null) {
          setIsModalLoading(false);
          setVerificationType(SUCCESS);
        }
      })
      .catch(({ response }) => {
        if (response.status === 422) {
          setIsModalLoading(false);
          setVerificationType(DANGER);
        }
      });
  };

  const onUserDeleteClick = (user) => {
    setSelectedUser(user);
    setIsDeactivateModalOpen(true);
  };

  const onUserDelete = () => {
    deleteUser(selectedUser.id)
      .then(() => {
        setIsDeactivateModalOpen(false);
        setIsDeactivateConfirmationModalOpen(true);
        setVerificationType(SUCCESS);
      })
      .catch(() => {
        setIsDeactivateModalOpen(false);
        setIsDeactivateConfirmationModalOpen(true);
        setVerificationType(DANGER);
      });
  };

  const onUserReactivateClick = (user) => {
    setSelectedUser(user);
    setIsReactivateModalOpen(true);
  };

  const onUserReactivate = () => {
    reactivateUser(selectedUser.id)
      .then(() => {
        setIsReactivateModalOpen(false);
        setIsReactivateConfirmationModalOpen(true);
        setVerificationType(SUCCESS);
        fetchUsers();
      })
      .catch(() => {
        setIsReactivateModalOpen(false);
        setIsReactivateConfirmationModalOpen(true);
        setVerificationType(DANGER);
      });
  };

  const onResendInvitation = (user) => {
    setSelectedUser(user);
    setIsResendInviteModalOpen(true);
    setIsModalLoading(true);
    resendInvite(user.id)
      .then(({ data }) => {
        if (data.error === null) {
          setIsModalLoading(false);
          setVerificationType(SUCCESS);
        }
      })
      .catch(({ response }) => {
        if (response.status === 422) {
          setIsModalLoading(false);
          setVerificationType(DANGER);
        }
      });
  };

  const onSuccessSubHeader = () => {
    return (
      <SanitizedHTML
        html={
          status === PENDING_STATE
            ? I18n.t(
                'components.affiliate_three_sixty.affiliate.users.delete.success.message',
              )
            : I18n.t(
                'components.affiliate_three_sixty.affiliate.users.deactivate.success.message',
              )
        }
      />
    );
  };

  const onFailSubHeader = () => {
    return (
      <SanitizedHTML
        html={
          status === PENDING_STATE
            ? I18n.t(
                'components.affiliate_three_sixty.affiliate.users.delete.fail.message',
              )
            : I18n.t(
                'components.affiliate_three_sixty.affiliate.users.deactivate.fail.message',
              )
        }
      />
    );
  };

  return (
    <>
      <Table className="affiliate-user-list__table">
        <TableHead>
          <TableRow>
            <TableHeader>
              <I18nText id="affiliate_three_sixty.affiliate.users.table.header.name" />
            </TableHeader>
            <TableHeader>
              <I18nText id="affiliate_three_sixty.affiliate.users.table.header.email" />
            </TableHeader>
            <TableHeader>
              <I18nText id="affiliate_three_sixty.affiliate.users.table.header.mobile_number" />
            </TableHeader>
            {status !== PENDING_STATE && (
              <TableHeader align="center">
                <I18nText id="affiliate_three_sixty.affiliate.users.table.header.verified" />
              </TableHeader>
            )}
            <TableHeader>
              <I18nText id="affiliate_three_sixty.affiliate.users.table.header.teams" />
            </TableHeader>
            {status === PENDING_STATE && (
              <TableHeader>
                <I18nText id="affiliate_three_sixty.affiliate.users.table.header.invited_on" />
              </TableHeader>
            )}
            {status === ACTIVE_STATE && (
              <TableHeader>
                <I18nText id="affiliate_three_sixty.affiliate.users.table.header.last_active" />
              </TableHeader>
            )}
            {status === DEACTIVATED_STATE && (
              <TableHeader>
                <I18nText id="affiliate_three_sixty.affiliate.users.table.header.deactivated_on" />
              </TableHeader>
            )}
            <TableHeader align="center">
              <I18nText id="affiliate_three_sixty.affiliate.users.table.header.action" />
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TablePlaceholder numberOfColumn={6} numberOfRows={10} />
          ) : (
            users.map((user) => (
              <UserTableRow
                key={user.id}
                user={user}
                onUserReactivateClick={onUserReactivateClick}
                processVerification={processVerification}
                onResendInvitation={onResendInvitation}
                onUserDeleteClick={onUserDeleteClick}
                signedInAsSuperAdmin={signedInAsSuperAdmin}
                permissionOptions={permissionOptions}
                permissionTemplateOptions={permissionTemplateOptions}
                currentUserId={currentUserId}
                userAffiliateId={userAffiliateId}
                status={status}
                fetchUsers={fetchUsers}
              />
            ))
          )}
        </TableBody>
      </Table>
      {!isLoading && !users.length && <EmptyTableContent />}
      <ConfirmationModal
        className="affiliate-user-list__confirmation-modal"
        confirmationHeader={verificationHeader()}
        subHeader={verificationSubHeader()}
        onTimeout={() => setIsModalOpen(false)}
        type={!isModalLoading && verificationType}
        isModalOpen={isModalOpen}
        withAction={!isModalLoading}
        timeout={100000}
        rejectionText={
          <I18nText id="affiliate_three_sixty.affiliate.users.verify.close" />
        }
        confirmationText={
          <I18nText id="affiliate_three_sixty.affiliate.users.verify.resend" />
        }
        onConfirm={() =>
          verificationType === DANGER
            ? processVerification(selectedUser)
            : processVerification(selectedUser, true)
        }
        onReject={() => setIsModalOpen(false)}
      />
      <ConfirmationModal
        confirmationHeader={
          status === PENDING_STATE ? (
            <I18nText id="affiliate_three_sixty.affiliate.users.delete.header" />
          ) : (
            <I18nText id="affiliate_three_sixty.affiliate.users.deactivate.header" />
          )
        }
        subHeader={
          <span className="affiliate-user-list__confirmation-message">
            <SanitizedHTML
              html={
                status === PENDING_STATE
                  ? I18n.t(
                      'components.affiliate_three_sixty.affiliate.users.delete.message',
                      {
                        user: selectedUser && selectedUser.name,
                      },
                    )
                  : I18n.t(
                      'components.affiliate_three_sixty.affiliate.users.deactivate.message',
                      {
                        user: selectedUser && selectedUser.name,
                      },
                    )
              }
            />
          </span>
        }
        type="warning"
        isModalOpen={isDeactivateModalOpen}
        withAction={!isModalLoading}
        rejectionText={
          <I18nText id="affiliate_three_sixty.affiliate.users.deactivate.cancel" />
        }
        confirmationText={
          status === PENDING_STATE ? (
            <I18nText id="affiliate_three_sixty.affiliate.users.delete.button" />
          ) : (
            <I18nText id="affiliate_three_sixty.affiliate.users.deactivate.button" />
          )
        }
        onConfirm={() => onUserDelete()}
        onReject={() => setIsDeactivateModalOpen(false)}
      />
      <ConfirmationModal
        confirmationHeader={
          verificationType === SUCCESS ? (
            <I18nText id="affiliate_three_sixty.affiliate.users.deactivate.success.header" />
          ) : (
            <I18nText id="affiliate_three_sixty.affiliate.users.deactivate.fail.header" />
          )
        }
        subHeader={
          verificationType === SUCCESS
            ? onSuccessSubHeader()
            : onFailSubHeader()
        }
        isConfirmationOnly={true}
        confirmationText={
          <I18nText id="affiliate_three_sixty.affiliate.users.verify.ok" />
        }
        onConfirm={() => setIsDeactivateConfirmationModalOpen(false)}
        type={verificationType}
        isModalOpen={isDeactivateConfirmationModalOpen}
      />
      <ConfirmationModal
        confirmationHeader={
          <I18nText id="affiliate_three_sixty.affiliate.users.reactivate.header" />
        }
        subHeader={
          <span className="affiliate-user-list__confirmation-message">
            <SanitizedHTML
              html={I18n.t(
                'components.affiliate_three_sixty.affiliate.users.reactivate.message',
                {
                  user: selectedUser && selectedUser.name,
                },
              )}
            />
          </span>
        }
        type="warning"
        isModalOpen={isReactivateModalOpen}
        withAction={!isModalLoading}
        rejectionText={
          <I18nText id="affiliate_three_sixty.affiliate.users.reactivate.cancel" />
        }
        confirmationText={
          <I18nText id="affiliate_three_sixty.affiliate.users.reactivate.button" />
        }
        onConfirm={() => onUserReactivate()}
        onReject={() => setIsReactivateModalOpen(false)}
      />
      <ConfirmationModal
        confirmationHeader={
          verificationType === SUCCESS ? (
            <I18nText id="affiliate_three_sixty.affiliate.users.reactivate.success.header" />
          ) : (
            <I18nText id="affiliate_three_sixty.affiliate.users.reactivate.fail.header" />
          )
        }
        subHeader={
          verificationType === SUCCESS ? (
            <SanitizedHTML
              html={I18n.t(
                'components.affiliate_three_sixty.affiliate.users.reactivate.success.message',
              )}
            />
          ) : (
            <SanitizedHTML
              html={I18n.t(
                'components.affiliate_three_sixty.affiliate.users.reactivate.fail.message',
              )}
            />
          )
        }
        isConfirmationOnly={true}
        confirmationText={
          <I18nText id="affiliate_three_sixty.affiliate.users.verify.ok" />
        }
        onConfirm={() => setIsReactivateConfirmationModalOpen(false)}
        type={verificationType}
        isModalOpen={isReactivateConfirmationModalOpen}
      />
      <ConfirmationModal
        className="affiliate-user-list__confirmation-modal"
        confirmationHeader={verificationHeader(true)}
        subHeader={verificationSubHeader(true)}
        onTimeout={() => {
          setIsResendInviteModalOpen(false);
          setVerificationType(null);
        }}
        type={verificationType}
        isModalOpen={isResendInviteModalOpen}
        timeout={5000}
        withAction={false}
      />
    </>
  );
};

UserTable.defaultProps = {
  permissionTemplateOptions: [],
};

UserTable.propTypes = {
  status: PropTypes.string.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  deleteUser: PropTypes.func.isRequired,
  reactivateUser: PropTypes.func.isRequired,
  resendInvite: PropTypes.func.isRequired,
  verifyEmail: PropTypes.func.isRequired,
  reverifyEmail: PropTypes.func.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  currentUserId: PropTypes.number.isRequired,
};

export default UserTable;
